import {
	DocumentsOfObjectDataGridApi
} from "src/api/generated/documents/api/documentsOfObjectDataGridApi.ts";
import {
	EditDocumentWithWopiButtonMenuItem
} from "src/components/views/documents/wopi/EditDocumentWithWopiButtonMenuItem.tsx";
import { AsyncButtonContextMenuItem } from "src/components/common/contextMenu/AsyncButtonContextMenuItem.tsx";
import { faCheck, faFilePlus, faMapPin, faUndo, faUpload } from "@fortawesome/pro-regular-svg-icons";
import { AavoDataGridRowContextMenuParams } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import i18n from "i18next";
import { AavoContextMenuContext } from "src/components/common/contextMenu/AavoContextMenu.tsx";
import { uploadFile } from "src/utils/fileUploading.ts";
import { DocumentUpdateApi } from "src/api/generated/documents/api/documentUpdateApi.ts";
import { AavoAsyncFileInputButton } from "src/components/common/buttons/AavoAsyncFileInputButton.tsx";
import { AavoContextMenuItem } from "src/components/common/contextMenu/AavoContextMenuItem";
import { DocumentRevisionApi } from "src/api/generated/documents/api/documentRevisionApi.ts";
import { DocumentRevisionsView } from "src/components/views/documents/revisions/DocumentRevisionsView.tsx";
import { SendDocumentsView } from "src/components/views/documents/mailing/SendDocumentsView.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/useTenantCustomizations.tsx";
import { GenericDialogContext } from "src/components/common/dialogs/GenericDialogProvider.tsx";
import { DocumentConnectionView } from "src/api/generated/postgres/db/types/documents/tables/documentConnectionView.ts";
import { useContextOrThrow } from "src/utils/useContextOrThrow.tsx";
import { AavoObjectRef } from "src/api/generated/common/sourceType/aavoObjectRef.ts";
import { faEnvelope, faFile } from "@fortawesome/pro-regular-svg-icons";

export interface DocumentsOfObjectDataGridContextMenuProps
	extends AavoDataGridRowContextMenuParams<DocumentConnectionView> {
	objectRef: AavoObjectRef,
	editable: boolean;
	refresh: () => Promise<unknown>;
}

export const DocumentsOfObjectDataGridContextMenu = (props: DocumentsOfObjectDataGridContextMenuProps) => {
	const {
		row,
		allSelectedRows,
		multipleRowsSelected,
		editable,
		refresh,
		objectRef
	} = props;

	const { closeContextMenu } = useContextOrThrow(AavoContextMenuContext);
	const { openDialog } = useContextOrThrow(GenericDialogContext);
	const tenantCustomizations = useTenantCustomizations();

	return [
		editable &&
		<EditDocumentWithWopiButtonMenuItem
			key={"edit_with_wopi"}
			fileExtension={row.primaryFileExtension ?? undefined}
			fileUuid={row.primaryFileUuid ?? undefined}
			visible={!multipleRowsSelected}
		/>,
		<AsyncButtonContextMenuItem
			key={"revisions"}
			label={i18n.t("revisions")}
			icon={faFile}
			visible={!multipleRowsSelected}
			onClick={async () => {
				openDialog(() => ({
					title: `${i18n.t("revisions")} (${row.description1})`,
					children: <DocumentRevisionsView
						editable={editable}
						document={row}
					/>
				}));
			}}
		/>,
		editable && (
			row.isPinned
				? <AsyncButtonContextMenuItem
					key={"unpin"}
					label={i18n.t("unpin")}
					icon={faUndo}
					visible={!multipleRowsSelected}
					onClick={async () => {
						await DocumentsOfObjectDataGridApi.setDocumentConnectionIsPinned({
							documentConnectionId: row.documentConnectionId,
							isPinned: false
						});
						await refresh();
					}} />
				: <AsyncButtonContextMenuItem
					key={"pin"}
					label={i18n.t("pin")}
					icon={faMapPin}
					visible={!multipleRowsSelected}
					onClick={async () => {
						await DocumentsOfObjectDataGridApi.setDocumentConnectionIsPinned({
							documentConnectionId: row.documentConnectionId,
							isPinned: true
						});
						await refresh();
					}} />
		),
		editable &&
		<AsyncButtonContextMenuItem
			key={"set_as_main_document"}
			label={i18n.t("set_as_main_document")}
			icon={faCheck}
			visible={!multipleRowsSelected}
			onClick={async () => {
				await DocumentsOfObjectDataGridApi.setDocumentToMainDocumentOfObject({
					objectRef: objectRef,
					documentId: row.documentId
				});
				await refresh();
			}} />,
		editable &&
		<AavoContextMenuItem key={"replace_current_revision_file"}>
			<AavoAsyncFileInputButton
				label={i18n.t("replace_current_revision_file")}
				multiple={false}
				icon={faUpload}
				onSubmit={async (file: File) => {
					const fileHandle = await uploadFile(file);
					await DocumentUpdateApi.updateDocumentActiveRevisionFile({
						documentId: row.documentId,
						newFile: fileHandle
					});
					closeContextMenu();
					await refresh();
				}}
			/>
		</AavoContextMenuItem>,
		editable &&
		<AavoContextMenuItem key={"create_a_new_revision"}>
			<AavoAsyncFileInputButton
				label={i18n.t("create_a_new_revision")}
				multiple={false}
				icon={faFilePlus}
				onSubmit={async (file: File) => {
					const fileHandle = await uploadFile(file);
					await DocumentRevisionApi.createDocumentRevision({
						documentId: row.documentId,
						file: fileHandle
					});
					closeContextMenu();
					await refresh();
				}}
			/>
		</AavoContextMenuItem>,
		<AsyncButtonContextMenuItem
			key={"send_selected_documents"}
			label={i18n.t("send_selected_documents")}
			icon={faEnvelope}
			onClick={async () => {
				openDialog(({ closeDialog }) => ({
					title: i18n.t("send_documents"),
					children: <SendDocumentsView documents={allSelectedRows}
												 onCompletedSuccessfully={() => {
													 closeDialog();
												 }}
					/>
				}));
			}} />,
		tenantCustomizations.documentsOfObjectDataGridContextMenuComponents?.(props)
	];
};